import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HouseIcon, FolderIcon, ListChecksIcon, UsersThreeIcon } from '@phosphor-icons/react';

interface SidebarNavigationProps {
  selectedCompanyId: string | null;
  canViewUserManagement: boolean;
}

export const SidebarNavigation = ({
  selectedCompanyId,
  canViewUserManagement,
}: SidebarNavigationProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const isActive = (path: string) => {
    if (path === '/') {
      return pathname === '/' || pathname.startsWith('/documents/filtered');
    }
    return pathname.startsWith(path);
  };

  const getNavItemClasses = (path: string, disabled = false) => {
    const baseClasses = 'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition';
    const activeClasses = isActive(path) ? 'bg-[#dee7ed]' : 'hover:bg-[#dee7ed]';
    const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
    return `${baseClasses} ${activeClasses} ${disabledClasses}`;
  };

  return (
    <nav className="px-3 flex flex-col gap-1">
      <button onClick={() => navigate('/')} className={getNavItemClasses('/')}>
        <HouseIcon size={20} weight="regular" className="text-primary" />
        <span className="text-sm font-medium text-text">{t('sidebar.dashboard')}</span>
      </button>

      <button
        onClick={() => navigate('/documents')}
        disabled={!selectedCompanyId}
        className={getNavItemClasses('/documents', !selectedCompanyId)}
      >
        <FolderIcon size={20} weight="regular" className="text-primary" />
        <span className="text-sm font-medium text-text">{t('sidebar.documents')}</span>
      </button>

      <button
        onClick={() => navigate('/tasks')}
        disabled={!selectedCompanyId}
        className={getNavItemClasses('/tasks', !selectedCompanyId)}
      >
        <ListChecksIcon size={20} weight="regular" className="text-primary" />
        <span className="text-sm font-medium text-text">{t('sidebar.tasks')}</span>
      </button>

      {canViewUserManagement && (
        <button
          onClick={() => navigate('/admin/users')}
          disabled={!selectedCompanyId}
          className={getNavItemClasses('/admin/users', !selectedCompanyId)}
        >
          <UsersThreeIcon size={20} weight="regular" className="text-primary" />
          <span className="text-sm font-medium text-text">{t('sidebar.admin')}</span>
        </button>
      )}
    </nav>
  );
};
